import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Skeleton,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CarOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import EmptyState from '../../components/EmptyState';
import { COLOR_PRIMARY, COLOR_WARNING, COLOR_ERROR } from '../../utils/colors';

const { Option } = Select;

const VanList = () => {
  const navigate = useNavigate();
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalVans: 0,
    inUseVans: 0,
    maintenanceVans: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  const fetchVans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        search: filters.search,
        status: filters.status,
      };
      const response = await apiService.vans.getAll(params);
      const vansData = response.data || [];
      setVans(Array.isArray(vansData) ? vansData : []);
      
      // Calculate statistics
      const totalVans = vansData.length;
      const inUseVans = vansData.filter(van => van.status === 'in-use').length;
      const maintenanceVans = vansData.filter(van => van.status === 'maintenance').length;
      const notAvailableVans = vansData.filter(van => van.status === 'not available').length;
      
      setStats({
        totalVans,
        inUseVans,
        maintenanceVans,
        notAvailableVans,
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load vans. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVans();
  }, [fetchVans]);

  const handleDelete = async (id) => {
    try {
      await apiService.vans.delete(id);
      message.success('Van deleted successfully');
      fetchVans();
    } catch (error) {
      console.error('Error deleting van:', error);
      message.error('Failed to delete van');
    }
  };


  const columns = [
    {
      title: 'Reference',
      dataIndex: 'registration',
      key: 'registration',
      render: (text, record) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: COLOR_PRIMARY, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          onClick={() => navigate(`/vans/${record._id}`)}
        >
          <Space>
            <CarOutlined />
            {text}
          </Space>
        </button>
      ),
    },
    {
      title: 'Make & Model',
      dataIndex: 'make',
      key: 'make',
      render: (text, record) => `${record.make} ${record.model}`,
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'Van Type',
      dataIndex: 'vanType',
      key: 'vanType',
      render: (type) => {
        if (type === 'company_van') return 'Company Van';
        if (type === 'rental') return 'Rental';
        if (type === 'finance') return 'Finance';
        if (type === 'contract_van') return 'Contract Van';
        if (type === 'external_van') return 'External Van';
        return type;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/vans/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this van?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Failed to load vans"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={fetchVans}>Retry</Button>}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card>
            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
              <Statistic title="Total Vans" value={stats.totalVans} prefix={<CarOutlined />} />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
              <Statistic title="In Use Vans" value={stats.inUseVans} prefix={<CarOutlined />} valueStyle={{ color: COLOR_PRIMARY }} />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
              <Statistic title="In Maintenance" value={stats.maintenanceVans} prefix={<ToolOutlined />} valueStyle={{ color: COLOR_WARNING }} />
            </Skeleton>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
              <Statistic title="Not Available" value={stats.notAvailableVans} prefix={<CarOutlined />} valueStyle={{ color: COLOR_ERROR }} />
            </Skeleton>
          </Card>
        </Col>
      </Row>

      <Card
        style={{ marginTop: 16 }}
        title="Vans"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/vans/create')}
          >
            New Van
          </Button>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search vans"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="available">Available</Option>
            <Option value="in-use">In Use</Option>
            <Option value="maintenance">Maintenance</Option>
            <Option value="not available">Not Available</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={vans}
          rowKey="_id"
          loading={loading}
          locale={{ emptyText: <EmptyState message="No vans found" hint="Add a van to get started" /> }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </Card>
    </div>
  );
};

export default VanList; 